import type { EmailQuotaStatus } from "@voice2mail/shared/v2m_api";
import { useCallback, useEffect, useState } from "react";

import * as api from "../api/client";

interface QuotaStatusProps {
  /** If provided, forces display of this quota (for inline embedding inside the wizard/progress views). */
  quota?: EmailQuotaStatus | null;
}

export function QuotaStatus({ quota: externalQuota }: QuotaStatusProps) {
  const [quota, setQuota] = useState<EmailQuotaStatus | null>(externalQuota ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!externalQuota);

  const fetchQuota = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getEmailQuota();
      setQuota(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load quota");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!externalQuota) {
      fetchQuota();
    }
  }, [externalQuota, fetchQuota]);

  useEffect(() => {
    if (externalQuota) {
      setQuota(externalQuota);
    }
  }, [externalQuota]);

  if (loading && !quota) {
    return <div className="text-sm text-slate-500 py-2">Loading quota…</div>;
  }

  if (error && !quota) {
    return (
      <div
        className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm"
        role="alert"
      >
        Error: {error}
      </div>
    );
  }

  if (!quota) return null;

  // Backend gap: GET /v1/admin/quota (campaigns.py's get_quota /
  // EmailQuotaStatus) never sends a daily limit, even though the underlying
  // EmailQuotaTracker knows one — flagged for v2m-backend (V2M-DEV-724).
  // Degrade honestly: show the raw sent count with no fraction/bar instead
  // of dividing by an invented limit.
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="quota-status">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Email Quota Today
        </span>
        <span className="text-sm font-semibold text-slate-900">
          {quota.totalSent.toLocaleString()}
        </span>
      </div>
      <div className="flex gap-4 mt-2 text-xs text-slate-500">
        <span>Transactional: {quota.transactionalSent}</span>
        <span>Bulk: {quota.bulkSent}</span>
        <span>Bulk available: {quota.bulkAvailable.toLocaleString()}</span>
      </div>
    </div>
  );
}
